import React, { useLayoutEffect, useRef, useState } from 'react';
import './appearance.css';

// Keep appearance local to this browser; unavailable storage must not block the app.
const STORAGE_KEY = 'escort.appearance.v1';
const DEFAULTS = { theme: 'dark', contrast: false, textSize: 'normal' };
function readPreferences() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return {
      theme: saved?.theme === 'light' ? 'light' : 'dark',
      contrast: saved?.contrast === true,
      textSize: ['normal', 'large', 'extra-large'].includes(saved?.textSize)
        ? saved.textSize : 'normal',
    };
  } catch { return DEFAULTS; }
}

export default function AppearanceControls() {
  const [preferences, setPreferences] = useState(readPreferences);
  const [open, setOpen] = useState(false);
  const container = useRef(null);
  const trigger = useRef(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = preferences.theme;
    root.dataset.contrast = preferences.contrast ? 'high' : 'normal';
    root.dataset.textSize = preferences.textSize;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences)); } catch { /* optional */ }
  }, [preferences]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!container.current?.contains(event.target)) setOpen(false);
    };
    const closeEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [open]);

  const update = (key, value) => setPreferences((current) => ({ ...current, [key]: value }));
  return (
    <div className="appearance" ref={container}>
      <button ref={trigger} className="appearance-trigger" type="button"
        aria-label="Appearance" title="Appearance"
        aria-expanded={open} aria-controls="appearance-panel"
        onClick={() => setOpen((value) => !value)}>
        <span aria-hidden="true">Aa</span> <span className="appearance-label">Appearance</span>
      </button>
      {open && (
        <section id="appearance-panel" className="appearance-panel" aria-label="Appearance settings">
          <h2>Appearance</h2>
          <fieldset>
            <legend>Theme</legend>
            <div className="appearance-options">
              {['dark', 'light'].map((theme) => (
                <label key={theme}>
                  <input type="radio" name="appearance-theme" value={theme}
                    checked={preferences.theme === theme} onChange={() => update('theme', theme)} />
                  {theme === 'dark' ? 'Dark' : 'Light'}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="appearance-contrast">
            <input type="checkbox" checked={preferences.contrast}
              onChange={(event) => update('contrast', event.target.checked)} />
            High contrast
          </label>
          <fieldset>
            <legend>Text size</legend>
            <div className="appearance-options appearance-sizes">
              {[['normal', 'Normal'], ['large', 'Large'], ['extra-large', 'Extra large']].map(([value, label]) => (
                <label key={value}>
                  <input type="radio" name="appearance-text" value={value}
                    checked={preferences.textSize === value} onChange={() => update('textSize', value)} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
        </section>
      )}
    </div>
  );
}
